import { db } from '@/config/firebase'
import firebase from 'firebase'

const timestamp = firebase.firestore.Timestamp

/**
 * User
 */
function getUserRef(userId) {
  return db.collection('users').doc(userId)
}

/**
 * User Notifications
 */
function getUserNotifRef(userId, deviceId) {
  return getUserRef(userId)
    .collection('notifications')
    .doc(deviceId)
}

function getUserNotifs(userId) {
  return getUserRef(userId)
    .collection('notifications')
    .get()
}

function updateUserNotif(userId, deviceId, notif) {
  return getUserNotifRef(userId, deviceId).set(notif, { merge: true })
}

/**
 * User Receivers
 */
function getUserReceiverRef(userId, receiverId) {
  return getUserRef(userId)
    .collection('receivers')
    .doc(receiverId)
}

function addUserReceiver(userId, receiver) {
  return getUserRef(userId)
    .collection('receivers')
    .add(receiver)
}

function updateUserReceiver(userId, receiverId, receiver) {
  return getUserReceiverRef(userId, receiverId).set(receiver, { merge: true })
}

function deleteUserReceiver(userId, receiverId) {
  return getUserReceiverRef(userId, receiverId).delete()
}

/**
 * Content
 */
function addContent(content) {
  return db.collection('contents').add(content)
}

function getContent(id) {
  return db
    .collection('contents')
    .doc(id)
    .get()
}

function updateContent(id, content, currentTime = false) {
  if (currentTime) {
    content.updated = timestamp.now()
  }

  return db
    .collection('contents')
    .doc(id)
    .update(content)
}

function deleteContent(id) {
  return db
    .collection('contents')
    .doc(id)
    .delete()
}

/**
 * Device
 */
function getDeviceRef(id) {
  return db.collection('devices').doc(id)
}

function getDevice(id) {
  return getDeviceRef(id).get()
}

function getDevices(userId) {
  return db
    .collection('devices')
    .where('owner', '==', userId)
    .get()
}

function updateDevice(id, device) {
  return getDeviceRef(id).update(device)
}

function upsertDeviceProps(deviceId, props) {
  return getDeviceRef(deviceId).set(props, { merge: true })
}

function deleteDevice(id) {
  return getDeviceRef(id).delete()
}

/**
 * Device playback
 */
function getPlaybacks(deviceId) {
  return getDeviceRef(deviceId)
    .collection('playback')
    .get()
}

/**
 * Device logs
 */
function getDeviceLogs(deviceId) {
  return getDeviceRef(deviceId)
    .collection('logs')
    .get()
}

/**
 * Device background
 */
function addBackground(deviceId, background) {
  background.timestamp = timestamp.now()

  return getDeviceRef(deviceId)
    .collection('backgrounds')
    .add(background)
}

function updateBackground(deviceId, backgroundId, background) {
  return getDeviceRef(deviceId)
    .collection('backgrounds')
    .doc(backgroundId)
    .update(background)
}

function deleteBackGround(deviceId, backgroundId) {
  return getDeviceRef(deviceId)
    .collection('backgrounds')
    .doc(backgroundId)
    .delete()
}

/**
 * Device Unit
 */
function getDeviceUnitsByUserId(userId) {
  return db
    .collection('deviceUnits')
    .where('owner', '==', userId)
    .get()
}

function addDeviceUnit(doc, currentTime = false) {
  if (currentTime) {
    const now = timestamp.now()
    doc.created = now
    doc.updated = now
  }

  return db.collection('deviceUnits').add(doc)
}

function updateDeviceUnit(id, doc, currentTime = false) {
  if (currentTime) {
    doc.updated = timestamp.now()
  }
  return db
    .collection('deviceUnits')
    .doc(id)
    .update(doc)
}

function deleteDeviceUnit(id) {
  return db
    .collection('deviceUnits')
    .doc(id)
    .delete()
}

/**
 * Device Group
 */
function getDeviceGroupsByUserId(userId) {
  return db
    .collection('deviceGroups')
    .where('owner', '==', userId)
    .get()
}

function addDeviceGroup(doc, currentTime = false) {
  if (currentTime) {
    const now = timestamp.now()
    doc.created = now
    doc.updated = now
  }

  return db.collection('deviceGroups').add(doc)
}

function updateDeviceGroup(id, doc, currentTime = false) {
  if (currentTime) {
    doc.updated = timestamp.now()
  }

  return db
    .collection('deviceGroups')
    .doc(id)
    .update(doc)
}

function deleteDeviceGroup(id) {
  return db
    .collection('deviceGroups')
    .doc(id)
    .delete()
}

/**
 * Playlists
 */
function addPlaylist(playlist, currentTime = false) {
  if (currentTime) {
    playlist.updated = timestamp.now()
  }

  return db.collection('playlists').add(playlist)
}

function updatePlaylist(id, playlist, currentTime = false) {
  if (currentTime) {
    playlist.updated = timestamp.now()
  }

  return db
    .collection('playlists')
    .doc(id)
    .update(playlist)
}

function deletePlaylist(id) {
  return db
    .collection('playlists')
    .doc(id)
    .delete()
}

/**
 * Schedules
 */
function getScheduleRef(id) {
  return db.collection('schedules').doc(id)
}

function addSchedule(schedule, currentTime = false) {
  if (currentTime) {
    const now = timestamp.now()
    schedule.created = now
    schedule.updated = now
  }

  return db.collection('schedules').add(schedule)
}

function getSchedules(userId) {
  return db
    .collection('schedules')
    .where('owner', '==', userId)
    .get()
}

function updateSchedule(id, schedule, currentTime = false) {
  if (currentTime) {
    schedule.updated = timestamp.now()
  }

  return getScheduleRef(id).update(schedule)
}

function deleteSchedule(id) {
  return getScheduleRef(id).delete()
}

/**
 * Schedule Events
 */
function addScheduleEvent(scheduleId, scheduleEvent, currentTime = false) {
  const scheduleRef = getScheduleRef(scheduleId)
  if (currentTime) {
    scheduleRef.update({ updated: timestamp.now() })
  }

  return scheduleRef.collection('events').add(scheduleEvent)
}

function getScheduleEvents(scheduleId) {
  return getScheduleRef(scheduleId)
    .collection('events')
    .get()
}

function updateScheduleEvent(
  scheduleId,
  scheduleEventId,
  scheduleEvent,
  currentTime = false
) {
  const scheduleRef = getScheduleRef(scheduleId)
  if (currentTime) {
    scheduleRef.update({ updated: timestamp.now() })
  }

  return scheduleRef
    .collection('events')
    .doc(scheduleEventId)
    .update(scheduleEvent)
}

function deleteScheduleEvent(scheduleId, scheduleEventId, currentTime = false) {
  const scheduleRef = getScheduleRef(scheduleId)
  if (currentTime) {
    scheduleRef.update({ updated: timestamp.now() })
  }

  return scheduleRef
    .collection('events')
    .doc(scheduleEventId)
    .delete()
}

/**
 * Reports
 */
function updateReport(deviceId, report) {
  return db
    .collection('reports')
    .doc(deviceId)
    .set(report, { merge: true })
}

function getReportDetailsWithinByUnixtime(
  deviceId,
  reportType,
  startSeconds,
  endSeconds
) {
  return db
    .collection('reports')
    .doc(deviceId)
    .collection('details')
    .where('info.type', '==', reportType)
    .orderBy('info.timestamp.seconds', 'asc')
    .startAt(startSeconds)
    .endAt(endSeconds)
    .get()
}

function getReportDetailsWithin(deviceId, reportType, startDate, endDate) {
  return db
    .collection('reports')
    .doc(deviceId)
    .collection('details')
    .where('info.type', '==', reportType)
    .orderBy('info.timestamp', 'asc')
    .startAt(startDate)
    .endAt(endDate)
    .get()
}

/**
 * Helper functions to use firebase.firestore()
 */
function createBatch() {
  return db.batch()
}

export {
  /** User Notification */
  getUserNotifs,
  updateUserNotif,
  /** User Receiver */
  addUserReceiver,
  updateUserReceiver,
  deleteUserReceiver,
  /** Contents */
  addContent,
  getContent,
  updateContent,
  deleteContent,
  /** Devices */
  getDeviceRef,
  getDevice,
  getDevices,
  updateDevice,
  upsertDeviceProps,
  deleteDevice,
  /** Device playbacks */
  getPlaybacks,
  /** Device logs */
  getDeviceLogs,
  /** Device background */
  addBackground,
  updateBackground,
  deleteBackGround,
  /** Device Units */
  getDeviceUnitsByUserId,
  addDeviceUnit,
  updateDeviceUnit,
  deleteDeviceUnit,
  /** Device Goruns */
  getDeviceGroupsByUserId,
  addDeviceGroup,
  updateDeviceGroup,
  deleteDeviceGroup,
  /** Playlists */
  addPlaylist,
  updatePlaylist,
  deletePlaylist,
  /** Schedules */
  addSchedule,
  getSchedules,
  updateSchedule,
  deleteSchedule,
  /** Schedule events */
  addScheduleEvent,
  getScheduleEvents,
  updateScheduleEvent,
  deleteScheduleEvent,
  /** Reports */
  updateReport,
  getReportDetailsWithinByUnixtime,
  getReportDetailsWithin,
  /** Helper functions */
  createBatch
}
